import { useRef, useEffect, useCallback } from 'react';

export interface SwipeGestureOptions {
  /** Minimum horizontal distance (px) to trigger swipe. Default: 50 */
  threshold?: number;
  /** Maximum vertical distance (px) to allow. Default: 100 */
  maxVertical?: number;
  /** Prevent default touch behavior. Default: false */
  preventDefault?: boolean;
  /** Whether swipe detection is enabled. Default: true */
  enabled?: boolean;
}

export interface SwipeGestureHandlers {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
}

interface TouchState {
  startX: number;
  startY: number;
  startTime: number;
}

/**
 * Hook for detecting horizontal swipe gestures
 *
 * @param handlers - Callback functions for swipe events
 * @param options - Configuration options
 * @returns ref to attach to the swipeable element
 *
 * @example
 * const swipeRef = useSwipeGesture({
 *   onSwipeLeft: () => console.log('Swiped left'),
 *   onSwipeRight: () => console.log('Swiped right'),
 * });
 *
 * return <div ref={swipeRef}>Swipeable content</div>;
 */
export function useSwipeGesture<T extends HTMLElement = HTMLElement>(
  handlers: SwipeGestureHandlers,
  options: SwipeGestureOptions = {}
) {
  const { threshold = 50, maxVertical = 100, preventDefault = false, enabled = true } = options;

  const elementRef = useRef<T>(null);
  const touchState = useRef<TouchState | null>(null);
  const handlersRef = useRef(handlers);

  // Keep handlers ref updated to avoid stale closures
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!enabled) return;

      const touch = e.touches[0];
      if (!touch) return;

      touchState.current = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
      };
    },
    [enabled]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!enabled || !touchState.current) return;

      // Check if we should prevent default (horizontal scroll prevention)
      if (preventDefault) {
        const touch = e.touches[0];
        if (!touch) return;

        const deltaX = Math.abs(touch.clientX - touchState.current.startX);
        const deltaY = Math.abs(touch.clientY - touchState.current.startY);

        // If horizontal movement is dominant, prevent default
        if (deltaX > deltaY && deltaX > 10) {
          e.preventDefault();
        }
      }
    },
    [enabled, preventDefault]
  );

  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!enabled || !touchState.current) return;

      const touch = e.changedTouches[0];
      if (!touch) {
        touchState.current = null;
        return;
      }

      const deltaX = touch.clientX - touchState.current.startX;
      const deltaY = Math.abs(touch.clientY - touchState.current.startY);
      const elapsed = Date.now() - touchState.current.startTime;

      // Reset touch state
      touchState.current = null;

      // Validate swipe conditions
      // - Must be primarily horizontal (not too much vertical)
      // - Must exceed threshold
      // - Must complete within 500ms (not too slow)
      if (deltaY > maxVertical || Math.abs(deltaX) < threshold || elapsed > 500) {
        return;
      }

      if (deltaX > 0) {
        handlersRef.current.onSwipeRight?.();
      } else {
        handlersRef.current.onSwipeLeft?.();
      }
    },
    [enabled, threshold, maxVertical]
  );

  const handleTouchCancel = useCallback(() => {
    touchState.current = null;
  }, []);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled) return;

    // Use passive: false only for touchmove if we need preventDefault
    const touchMoveOptions = preventDefault ? { passive: false } : { passive: true };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, touchMoveOptions);
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    element.addEventListener('touchcancel', handleTouchCancel, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [
    enabled,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,
    preventDefault,
  ]);

  return elementRef;
}
